import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { initFaro } from '@/lib/faro';

initFaro();

import { useAuth } from '@/hooks/useAuth';
import AppLayout from '@/layouts/AppLayout';
import AuthLayout from '@/layouts/AuthLayout';
import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import DashboardPage from '@/pages/DashboardPage';
import MarketplacePage from '@/pages/MarketplacePage';
import TemplateDetailPage from '@/pages/TemplateDetailPage';
import ContractsPage from '@/pages/ContractsPage';
import ContractDetailPage from '@/pages/ContractDetailPage';
import AmortizationPage from '@/pages/AmortizationPage';
import NotificationsPage from '@/pages/NotificationsPage';
import SettingsPage from '@/pages/SettingsPage';
import MySubsPage from '@/pages/MySubsPage';
import TemplateCreatePage from '@/pages/TemplateCreatePage';
import BespokeContractPage from '@/pages/BespokeContractPage';
import WallOfShamePage from '@/pages/WallOfShamePage';
import FeeTaxFeedPage from '@/pages/FeeTaxFeedPage';
import UserProfilePage from '@/pages/UserProfilePage';
import PilloryPage from '@/pages/PilloryPage';
import PillorySharePage from '@/pages/PillorySharePage';
import LandingPage from '@/pages/LandingPage';
import SocialAuthCallbackPage from '@/pages/SocialAuthCallbackPage';
import OAuthAuthorizePage from '@/pages/OAuthAuthorizePage';
import WebhooksPage from '@/pages/WebhooksPage';
import DeveloperPage from '@/pages/DeveloperPage';
import ExtensionsPage from '@/pages/ExtensionsPage';
import ContractEditPage from '@/pages/ContractEditPage';
import DiscoverMembersPage from '@/pages/DiscoverMembersPage';
import MyTemplatesPage from '@/pages/MyTemplatesPage';
import TemplateEditPage from '@/pages/TemplateEditPage';
import PrivacyPolicyPage from '@/pages/PrivacyPolicyPage';
import TermsOfServicePage from '@/pages/TermsOfServicePage';
import DocsPage from '@/pages/DocsPage';
import ShareLinkPage from '@/pages/ShareLinkPage';
import MessagesPage from '@/pages/MessagesPage';
import { InstallPrompt } from '@/components/InstallPrompt';
import { ObservabilityProvider } from '@/components/ObservabilityProvider';
import { ToastProvider } from '@/components/ui/toast';

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 10_000,
            refetchOnWindowFocus: true,
            retry: 1,
        },
    },
});

function ProtectedRoute({ children }: { children: React.ReactNode }) {
    const { user, isLoading } = useAuth();

    if (isLoading) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <p className="text-sm text-zinc-500">Loading...</p>
            </div>
        );
    }

    if (!user) return <Navigate to="/" replace />;
    return <>{children}</>;
}

function GuestRoute({ children }: { children: React.ReactNode }) {
    const { user, token, isLoading } = useAuth();

    // Only show loading if there's a token (might be logged in)
    if (isLoading && token) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <p className="text-sm text-zinc-500">Loading...</p>
            </div>
        );
    }

    if (user) return <Navigate to="/dashboard" replace />;
    return <>{children}</>;
}

function HomeRoute() {
    const { user, isLoading } = useAuth();

    if (isLoading) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <p className="text-sm text-zinc-500">Loading...</p>
            </div>
        );
    }

    if (user) return <Navigate to="/dashboard" replace />;
    return <LandingPage />;
}

function AppInit({ children }: { children: React.ReactNode }) {
    const { fetchUser } = useAuth();

    useEffect(() => {
        fetchUser();
    }, [fetchUser]);

    return <>{children}</>;
}

function App() {
    return (
        <QueryClientProvider client={queryClient}>
            <BrowserRouter>
                <AppInit>
                    <ObservabilityProvider>
                    <ToastProvider>
                    <InstallPrompt />
                    <Routes>
                        {/* Landing page */}
                        <Route path="/" element={<HomeRoute />} />

                        {/* Social auth callback */}
                        <Route path="/auth/:provider/callback" element={<SocialAuthCallbackPage />} />
                        <Route path="/oauth/authorize" element={<OAuthAuthorizePage />} />

                        {/* Public pages */}
                        <Route path="/privacy" element={<div className="min-h-screen bg-slate-950 px-5 py-8 sm:px-6"><PrivacyPolicyPage /></div>} />
                        <Route path="/terms" element={<div className="min-h-screen bg-slate-950 px-5 py-8 sm:px-6"><TermsOfServicePage /></div>} />
                        <Route path="/docs" element={<div className="min-h-screen bg-slate-950 px-5 py-8 sm:px-6"><div className="mx-auto max-w-5xl"><DocsPage /></div></div>} />
                        <Route path="/pillory/share/:token" element={<div className="min-h-screen bg-slate-950 px-5 py-8 sm:px-6"><div className="mx-auto max-w-5xl"><PillorySharePage /></div></div>} />
                        <Route path="/share/:token" element={<div className="min-h-screen bg-slate-950 px-5 py-8 sm:px-6"><div className="mx-auto max-w-5xl"><ShareLinkPage /></div></div>} />

                        {/* Guest routes */}
                        <Route element={<GuestRoute><AuthLayout /></GuestRoute>}>
                            <Route path="/login" element={<LoginPage />} />
                            <Route path="/register" element={<RegisterPage />} />
                        </Route>

                        {/* Protected routes */}
                        <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
                            <Route path="/dashboard" element={<DashboardPage />} />
                            <Route path="/marketplace" element={<MarketplacePage />} />
                            <Route path="/marketplace/:id" element={<TemplateDetailPage />} />
                            <Route path="/contracts" element={<ContractsPage />} />
                            <Route path="/contracts/:id" element={<ContractDetailPage />} />
                            <Route path="/contracts/:id/edit" element={<ContractEditPage />} />
                            <Route path="/contracts/:id/schedule" element={<AmortizationPage />} />
                            <Route path="/my-subs" element={<MySubsPage />} />
                            <Route path="/subs/:subId/new-contract" element={<BespokeContractPage />} />
                            <Route path="/my-templates" element={<MyTemplatesPage />} />
                            <Route path="/templates/create" element={<TemplateCreatePage />} />
                            <Route path="/templates/:id/edit" element={<TemplateEditPage />} />
                            <Route path="/wall-of-shame" element={<WallOfShamePage />} />
                            <Route path="/feed" element={<FeeTaxFeedPage />} />
                            <Route path="/pillory" element={<PilloryPage />} />
                            <Route path="/discover" element={<DiscoverMembersPage />} />
                            <Route path="/messages" element={<MessagesPage />} />
                            <Route path="/notifications" element={<NotificationsPage />} />
                            <Route path="/profiles/:id" element={<UserProfilePage />} />
                            <Route path="/profile" element={<Navigate to="/settings" replace />} />
                            <Route path="/settings" element={<SettingsPage />} />
                            <Route path="/webhooks" element={<WebhooksPage />} />
                            <Route path="/developer" element={<DeveloperPage />} />
                            <Route path="/extensions" element={<ExtensionsPage />} />
                        </Route>

                        {/* Catch all */}
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                    </ToastProvider>
                    </ObservabilityProvider>
                </AppInit>
            </BrowserRouter>
        </QueryClientProvider>
    );
}

createRoot(document.getElementById('app')!).render(
    <StrictMode>
        <App />
    </StrictMode>
);
